// AUTHORITATIVE analytics contract. Synced by scripts/tracking-sync.mjs into
// unbreakable/frontend/src/lib/analytics-contracts.ts and
// unbreakable-lander/src/lib/analytics-contracts.ts. Must remain import-free.

export const ANALYTICS_SOURCES = {
  CLIENT: 'client',
  SERVER: 'server',
} as const;

export const ANALYTICS_ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
} as const;

export const ORIGIN_PRODUCTS = {
  LANDER: 'lander',
  APP: 'app',
} as const;

export const EMITTERS = {
  BROWSER: 'browser',
  SERVER: 'server',
} as const;

export const CLIENT_EVENTS = [
  'page_viewed',
  'cta_clicked',
  'pricing_viewed',
  'schools_viewed',
  'plan_selected',
  'checkout_started',
  'checkout_completed',
  'contact_submitted',
  'signup_completed',
  'logged_in',
  'scroll_depth',
  'experiment_viewed',
] as const;

export const SERVER_EVENTS = [
  'account_created',
  'setup_completed',
  'subscription_status_changed',
  'subscription_canceled',
  'subscription_cancel_scheduled',
  'subscription_cancel_unscheduled',
  'device_registered',
  'supervision_failed',
  'rule_created',
  'checkout_started',
  'checkout_completed',
  'contact_submitted',
] as const;

export type ClientEventName = (typeof CLIENT_EVENTS)[number];
export type ServerEventName = (typeof SERVER_EVENTS)[number];
export type AnalyticsSource = (typeof ANALYTICS_SOURCES)[keyof typeof ANALYTICS_SOURCES];
export type AnalyticsEnvironment = (typeof ANALYTICS_ENVIRONMENTS)[keyof typeof ANALYTICS_ENVIRONMENTS];
export type OriginProduct = (typeof ORIGIN_PRODUCTS)[keyof typeof ORIGIN_PRODUCTS];
export type Emitter = (typeof EMITTERS)[keyof typeof EMITTERS];

export const ANALYTICS_EVENTS = {
  CLIENT: {
    PAGE_VIEWED: 'page_viewed',
    CTA_CLICKED: 'cta_clicked',
    PRICING_VIEWED: 'pricing_viewed',
    SCHOOLS_VIEWED: 'schools_viewed',
    PLAN_SELECTED: 'plan_selected',
    CHECKOUT_STARTED: 'checkout_started',
    CHECKOUT_COMPLETED: 'checkout_completed',
    CONTACT_SUBMITTED: 'contact_submitted',
    SIGNUP_COMPLETED: 'signup_completed',
    LOGGED_IN: 'logged_in',
    SCROLL_DEPTH: 'scroll_depth',
    EXPERIMENT_VIEWED: 'experiment_viewed',
  },
  SERVER: {
    ACCOUNT_CREATED: 'account_created',
    SETUP_COMPLETED: 'setup_completed',
    SUBSCRIPTION_STATUS_CHANGED: 'subscription_status_changed',
    SUBSCRIPTION_CANCELED: 'subscription_canceled',
    SUBSCRIPTION_CANCEL_SCHEDULED: 'subscription_cancel_scheduled',
    SUBSCRIPTION_CANCEL_UNSCHEDULED: 'subscription_cancel_unscheduled',
    DEVICE_REGISTERED: 'device_registered',
    SUPERVISION_FAILED: 'supervision_failed',
    RULE_CREATED: 'rule_created',
    CHECKOUT_STARTED: 'checkout_started',
    CHECKOUT_COMPLETED: 'checkout_completed',
    CONTACT_SUBMITTED: 'contact_submitted',
  },
} as const;

// Title-case Mixpanel display names for the marketing funnel events consumed
// by the lander and the backend track() fan-out. FROZEN — do not modify
// without updating the snapshot test in __tests__/contracts. These names
// drive Meta CAPI / GA4 / Google Ads dedup keys and Mixpanel dashboards.
export const MARKETING_MIXPANEL_NAMES = {
  page_viewed: 'Page Viewed',
  cta_clicked: 'CTA Clicked',
  pricing_viewed: 'Pricing Viewed',
  schools_viewed: 'Schools Viewed',
  scroll_depth: 'Scroll Depth',
  plan_selected: 'Plan Selected',
  checkout_started: 'Checkout Started',
  checkout_completed: 'Checkout Completed',
  contact_submitted: 'Contact Submitted',
  experiment_viewed: 'Experiment Viewed',
} as const;

export type MarketingEventName = keyof typeof MARKETING_MIXPANEL_NAMES;

// Server-emitted product events that need a Title Case Mixpanel display name.
// `satisfies Record<ProductServerEventName, string>` enforces full coverage —
// adding a new product event to SERVER_EVENTS without a display name here is
// a compile error. Funnel events (MarketingEventName) are excluded because
// their names live in MARKETING_MIXPANEL_NAMES.
export type ProductServerEventName = Exclude<ServerEventName, MarketingEventName>;

export const PRODUCT_MIXPANEL_NAMES = {
  account_created: 'Account Created',
  setup_completed: 'Setup Completed',
  subscription_status_changed: 'Subscription Status Changed',
  subscription_canceled: 'Subscription Canceled',
  subscription_cancel_scheduled: 'Subscription Cancel Scheduled',
  subscription_cancel_unscheduled: 'Subscription Cancel Unscheduled',
  device_registered: 'Device Registered',
  supervision_failed: 'Supervision Failed',
  rule_created: 'Rule Created',
} as const satisfies Record<ProductServerEventName, string>;

// Per-event payload version. Increment a specific event's number when you
// materially change its property shape; older data keeps its prior number.
export const EVENT_VERSIONS: Record<string, number> = {
  page_viewed: 1,
  cta_clicked: 1,
  pricing_viewed: 1,
  schools_viewed: 1,
  scroll_depth: 1,
  plan_selected: 1,
  checkout_started: 1,
  checkout_completed: 1,
  contact_submitted: 1,
  experiment_viewed: 1,
  signup_completed: 1,
  logged_in: 1,
  account_created: 1,
  setup_completed: 1,
  subscription_status_changed: 1,
  subscription_canceled: 1,
  subscription_cancel_scheduled: 1,
  subscription_cancel_unscheduled: 1,
  device_registered: 1,
  supervision_failed: 1,
  rule_created: 1,
};

export const DEFAULT_EVENT_VERSION = 1;

// Events that were emitted historically but are no longer emitted from any
// shipping code. Kept for historical-data lookup in dashboards and docs.
export const DEPRECATED_EVENTS = new Set<string>([
  'signup_completed',
  'login_submitted',
  'device_add_started',
  'rule_creation_started',
  'rule_creation_completed',
  'schedule_created',
  'schedule_edit_started',
  'checkout_settled',
  'supervision_started',
  'supervision_completed',
  'policy_assigned',
  'policy_removed',
  'schedule_updated',
]);

export const REQUIRED_BASE_PROPERTIES = [
  'event_id',
  'event_name',
  'occurred_at',
  'source',
  'environment',
  'origin_product',
  'emitter',
  'event_version',
] as const;

export function isClientEventName(value: string): value is ClientEventName {
  return (CLIENT_EVENTS as readonly string[]).includes(value);
}

export function isServerEventName(value: string): value is ServerEventName {
  return (SERVER_EVENTS as readonly string[]).includes(value);
}

export function getEventVersion(eventName: string): number {
  return EVENT_VERSIONS[eventName] ?? DEFAULT_EVENT_VERSION;
}

export function isDeprecatedEvent(eventName: string): boolean {
  return DEPRECATED_EVENTS.has(eventName);
}
